// Desc: Subscription & plans dashboard
import {
	Accordion,
	AccordionItem,
	Button,
	ButtonGroup,
	Chip,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	NumberInput,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tooltip
} from "@heroui/react";
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import PlanCard from '../../widgets/PlanCard.jsx';
import {useRecoilState} from 'recoil';
import {userState} from '../../store.jsx';
import {useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import moment from 'moment';
import toast from 'react-hot-toast';
import {motion} from 'framer-motion';

const PAID_PLANS = [3, 5, 7];

const CREDIT_GUIDE = [
	{icon: 'hgi-video-02', title: 'Video Dub', accent: 'violet', body: <><strong>2,000 credits</strong> per minute of dubbed video.</>},
	{icon: 'hgi-text', title: 'AI Speech', accent: 'sky', body: <><strong>1 credit</strong> / English-Indian char, <strong>2</strong> / Chinese-Japanese.</>},
	{icon: 'hgi-mic-01', title: 'Voice Clone', accent: 'emerald', body: <>Create a custom voice clone for <strong>12,000 credits</strong>.</>},
];

const FAQS = [
	{q: 'What is the text length limit for AI speech generation?', a: 'To ensure optimal model performance, each generation allows a maximum of 5,000 characters. If you have a very long text, you can easily break it into smaller sections and process them separately.'},
	{q: 'How are credits used for generating AI Speech?', a: 'Each English letter uses 1 credit. Characters from languages like Chinese or Japanese use 2 credits per character. You can check your credits in the bottom-left corner of the text box.'},
	{q: 'How are credits used for video dubbing?', a: 'Credits are based on video length. Video dubbing costs 2,000 credits per minute.'},
];

export default function Plans() {
	const [authMe] = useRecoilState(userState)
	const queryClient = useQueryClient()
	const [credits, setCredit] = useState(null)
	const [confirmSubscription, setConfirmSubscription] = useState(false)

	const plans = useQuery({
		queryKey: ['plans'],
		queryFn: repositories.subscription.plans
	});

	useEffect(() => {
		setTimeout(() => {
			queryClient.refetchQueries({queryKey: ['auth']})
		}, 1000)
	}, []);

	const getCurrentPlanID = useMemo(() => authMe?.current_plan || 1, [authMe.current_plan])
	const isPaid = useMemo(() => _.includes(PAID_PLANS, getCurrentPlanID), [getCurrentPlanID])

	const currentPlanName = useMemo(() => {
		const match = _.find(plans?.data?.plans, (p) => p.monthly_price_id === getCurrentPlanID || p.trial_id === getCurrentPlanID);
		return authMe?.current_plan_name || match?.name || (isPaid ? 'Pro Plan' : 'Free Trial');
	}, [plans?.data?.plans, getCurrentPlanID, authMe?.current_plan_name, isPaid])

	const purchaseCredit = useMutation({
		mutationFn: repositories.subscription.purchaseCredit,
		onSuccess: async () => {
			setConfirmSubscription(true)
		}
	})

	const manageBilling = useMutation({
		mutationFn: repositories.subscription.manageBilling,
		onSuccess: async (response) => {
			if (response?.session_url) {
				window.open(response?.session_url || '')
			} else {
				toast.error("Something went wrong, please try again")
			}
		}
	})

	const accentMap = {
		violet: 'from-violet-500/10 to-fuchsia-500/10 ring-violet-200/70 text-primary',
		sky: 'from-sky-500/10 to-indigo-500/10 ring-sky-200/70 text-sky-600',
		emerald: 'from-emerald-500/10 to-teal-500/10 ring-emerald-200/70 text-emerald-600',
	};

	return (
		<div className="min-h-72">
			{/* ── Current plan hero ── */}
			<motion.div
				initial={{opacity: 0, y: -10}}
				animate={{opacity: 1, y: 0}}
				transition={{type: 'spring', stiffness: 220, damping: 24}}
				className="relative overflow-hidden rounded-30 p-6 sm:p-8 bg-gradient-to-br from-[#5A0AE0] via-[#7B2CE2] to-[#8C2CE2] text-white"
			>
				<span aria-hidden className="pointer-events-none absolute -top-20 -left-10 h-56 w-56 rounded-full bg-fuchsia-400/30 blur-3xl"/>
				<span aria-hidden className="pointer-events-none absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-indigo-300/25 blur-3xl"/>

				<div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<p className="text-tiny font-medium uppercase tracking-wide text-white/70">Current plan</p>
						<div className="mt-1.5 flex items-center gap-2.5 flex-wrap">
							<h1 className="font-sans text-3xl font-semibold tracking-tight">{currentPlanName}</h1>
							<Chip
								size="sm" variant="flat"
								className="bg-white/20 text-white border border-white/30"
								startContent={<i className={`hgi-stroke ${isPaid ? 'hgi-crown' : 'hgi-sparkles'} text-sm ml-1`}/>}
							>
								{isPaid ? 'Active' : (authMe?.trial_status === 'expired' ? 'Trial expired' : 'Trial')}
							</Chip>
						</div>
						{_.includes([3, 5, 7], getCurrentPlanID) && authMe?.next_billing_date && (
							<p className="mt-2 text-sm text-white/75 flex items-center gap-1.5">
								<i className="hgi-stroke hgi-calendar-03 text-sm"/>
								Credits reset on {moment(authMe?.next_billing_date).format('Do MMMM, YYYY')}
							</p>
						)}
					</div>

					<div className="flex flex-wrap items-center gap-3">
						{_.includes([7], getCurrentPlanID) && (
							<ButtonGroup>
								<Popover placement="bottom-end" showArrow offset={20}>
									<PopoverTrigger>
										<Button radius="full" className="bg-white text-primary font-semibold">
											<i className="hgi-stroke hgi-add-01"></i>
											<span className="ml-1">Purchase credits</span>
										</Button>
									</PopoverTrigger>
									<PopoverContent>
										<div className="px-4 py-4 flex flex-col gap-2">
											<div className="text-medium font-bold text-gray-900">Purchase Credits</div>
											<div className="text-small text-gray-700">Note: 4000 credits, equal $1 USD.</div>
											<div className="flex gap-3 items-center">
												<label htmlFor="custom-credits" className="font-medium text-sm text-gray-700">Credits:</label>
												<NumberInput
													onValueChange={value => setCredit(value)}
													value={credits}
													step={1000} size="md"
													placeholder="****"
													minValue={4000}
												/>
											</div>
											<Button
												isDisabled={credits < 4000}
												isLoading={purchaseCredit.isPending}
												onPress={() => purchaseCredit.mutate({credits})}
												color="primary"
												className="mt-3 !rounded-xl">Purchase Credits</Button>
										</div>
									</PopoverContent>
								</Popover>
								<Tooltip showArrow color="foreground" content="This action only available for the pro plan">
									<Button isIconOnly radius="full" className="bg-white text-primary">
										<i className="hgi-stroke hgi-information-circle"></i>
									</Button>
								</Tooltip>
							</ButtonGroup>
						)}
						{_.includes([3, 5, 7], getCurrentPlanID) && (
							<Button
								radius="full"
								variant="bordered"
								className="text-white border-white/40 !opacity-100 font-sans"
								isLoading={manageBilling.isPending}
								onPress={manageBilling.mutate}
								startContent={!manageBilling.isPending && <i className="hgi-stroke hgi-list-setting text-lg"/>}
							>
								Manage billing
							</Button>
						)}
					</div>
				</div>

			</motion.div>

			{/* ── Plans grid ── */}
			<div className="mt-10 flex items-end justify-between gap-4 flex-wrap">
				<div>
					<h2 className="font-sans text-2xl font-semibold text-slate-800">Choose your plan</h2>
					<p className="text-sm text-slate-500">Upgrade anytime — credits scale with you.</p>
				</div>
			</div>

			{plans.isLoading ? (
				/* Skeleton grid mirrors the real layout so the page height stays stable (no layout shift on load) */
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-6">
					{Array.from({length: 4}).map((_, i) => (
						<div key={i} className="rounded-[26px] bg-white p-6 pl-7 ring-1 ring-slate-200/80 animate-pulse">
							<div className="h-2.5 w-16 rounded-full bg-slate-200"/>
							<div className="mt-3 h-6 w-28 rounded-lg bg-slate-200"/>
							<div className="mt-5 h-9 w-24 rounded-lg bg-slate-200"/>
							<div className="mt-6 h-px w-full bg-slate-100"/>
							<div className="mt-5 flex flex-col gap-3">
								{[90, 78, 84, 72, 80, 66].map((w, j) => (
									<div key={j} className="flex items-center gap-3">
										<span className="h-1.5 w-1.5 rotate-45 bg-slate-200"/>
										<span className="h-3 rounded bg-slate-200" style={{width: `${w}%`}}/>
									</div>
								))}
							</div>
							<div className="mt-6 h-12 w-full rounded-full bg-slate-200"/>
						</div>
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-6">
					{plans?.data?.plans.map((item, index) => (
						<motion.div
							key={item?.name}
							initial={{opacity: 0, y: 18}}
							animate={{opacity: 1, y: 0}}
							transition={{type: 'spring', stiffness: 260, damping: 24, delay: index * 0.06}}
						>
							<PlanCard planKey={index} data={item}/>
						</motion.div>
					))}
				</div>
			)}

			{/* ── How credits work ── */}
			<div className="mt-12">
				<h3 className="font-sans text-xl font-semibold text-slate-800 flex items-center gap-2">
					<i className="hgi-stroke hgi-coins-01 text-xl text-primary"/>
					How credits work
				</h3>
				<p className="text-sm text-slate-500 mt-1">A quick guide to how credits are spent across services.</p>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
					{CREDIT_GUIDE.map((c) => (
						<div key={c.title} className={`relative overflow-hidden rounded-22 bg-gradient-to-br ${accentMap[c.accent]} ring-1 p-5`}>
							<span className="grid h-11 w-11 place-items-center rounded-full bg-white/80 ring-1 ring-white shadow-sm">
								<i className={`hgi-stroke ${c.icon} text-xl`}/>
							</span>
							<h4 className="mt-4 font-sans text-md font-semibold text-slate-900">{c.title}</h4>
							<p className="mt-1 text-sm text-slate-600">{c.body}</p>
						</div>
					))}
				</div>
			</div>

			{/* ── FAQ ── */}
			<div className="mt-12">
				<h3 className="font-sans text-2xl font-semibold text-slate-800 mb-5">Frequently asked questions</h3>
				<Accordion variant="splitted">
					{FAQS.map((item, i) => (
						<AccordionItem key={i} className="font-medium" title={item.q}>
							<p className="text-sm text-slate-700 mb-4">{item.a}</p>
						</AccordionItem>
					))}
				</Accordion>
			</div>

			{/* Purchase-credits confirmation */}
			<Modal isOpen={confirmSubscription} onOpenChange={setConfirmSubscription}>
				<ModalContent>
					{(onClose) => (
						<>
							<ModalHeader>Purchase credits</ModalHeader>
							<ModalBody className="text-gray-600 text-center">
								<div className="text-center">
									<div className="flex items-center gap-2 justify-center text-2xl rounded-full bg-green-500 w-12 h-12 p-2 mx-auto text-white shadow-lg">
										<i className="hgi-stroke hgi-tick-01"/>
									</div>
								</div>
								<p>{purchaseCredit?.data?.message}</p>
							</ModalBody>
							<ModalFooter>
								<Button color="danger" variant="light" onPress={onClose}>Cancel</Button>
								<Button
									color="primary" variant="shadow"
									onPress={async () => {
										await window.open(purchaseCredit?.data?.payment_url, '_self')
										onClose()
									}}>
									Confirm
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
		</div>
	);
}
